import {createStyles, Select, Stack} from '@mantine/core';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {MinimalFolder} from 'constants/FolderConstants';

import deriveCapabilities from './eventTriggerConfigForm/deriveCapabilities';
import EventTypeSection from './eventTriggerConfigForm/EventTypeSection';
import LocationScopeSection from './eventTriggerConfigForm/LocationScopeSection';
import TargetScopeSection from './eventTriggerConfigForm/TargetScopeSection';
import {TriggerScope} from './eventTriggerConfigForm/types';
import {FolderScopeAccessStatus} from './eventTriggerConfigForm/useFolderScopeAccess';
import useRoutineSchemaOptions from './eventTriggerConfigForm/useRoutineSchemaOptions';
import {
  getModelTypeConstantForObjectType,
  getObjectTypeDisplayLabel,
  getObjectTypeUIConfig,
} from './objectTypeConfig';
import RoutineFieldHeader from './RoutineFieldHeader';
import {AllowedEventTrigger, EventTriggerConfig} from './RoutineTypes';
import {toCommonNounLabel} from './RoutineUtils';
import {FIELD_CHANGED, OBJECT_CREATED, TriggerKind} from './triggerConstants';

const SELECT_DROPDOWN_OFFSET_PX = 12;

interface EventTriggerConfigFormProps {
  config: Partial<EventTriggerConfig>;
  onChange: (updates: Partial<EventTriggerConfig>) => void;
  allowedTriggers: Array<AllowedEventTrigger>;
  locationScopeAccessStatus?: FolderScopeAccessStatus;
  locationScopeFolders?: Array<MinimalFolder>;
}

const useStyles = createStyles((theme) => ({
  section: {
    gap: theme.spacing.xs,
  },
  selectDropdown: {
    // Select does not expose its internal Popover offset. Cancel Mantine's
    // default 8px offset plus the Nucleus default 8px arrow adjustment.
    '&[data-position^="bottom"]': {
      marginTop: -SELECT_DROPDOWN_OFFSET_PX,
    },
    '&[data-position^="top"]': {
      marginTop: SELECT_DROPDOWN_OFFSET_PX,
    },
  },
  card: {
    cursor: 'pointer',
  },
  selectedCard: {
    backgroundColor: theme.colors.blue[0],
    borderColor: theme.colors.blue[7],
  },
  disabledCard: {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  searchInputContainer: {
    marginLeft: theme.spacing.lg,
    // Align SearchInput disabled surface with Mantine disabled inputs in this form.
    '& .chipSelectInput.is-disabled': {
      backgroundColor: theme.colors.gray[1],
      borderColor: theme.colors.gray[4],
    },
    // ChipSelectInput renders an inner disabled input with its own gray background.
    // Make it transparent so the outer disabled surface matches Mantine inputs.
    '& .chipSelectInput.is-disabled .chipSelectInput-input': {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
    },
  },
  nestedEventInputContainer: {
    marginTop: theme.spacing.xs,
  },
}));

// Derives the current scope from the stored config. `object_ids`/`schema_id` map directly to the
// instance/schema scopes. Once a data type is selected with neither set, the routine targets the
// "all" scope — except during a fresh data-type selection (`awaitingScopeSelection`), where the
// scope stays unselected until the user explicitly picks a target. (`trigger_kind` is intentionally
// not consulted: a data type that supports both triggers has no resolved `trigger_kind` when the
// user picks the "all" scope, and requiring it would drop that explicit selection.)
function deriveScope(
  config: Partial<EventTriggerConfig>,
  awaitingScopeSelection: boolean
): TriggerScope | undefined {
  if (config.object_ids != null) {
    return 'instance';
  }
  if (config.schema_id != null) {
    return 'schema';
  }
  if ((config.object_type ?? '') === '' || awaitingScopeSelection) {
    return undefined;
  }
  return 'all';
}

export default function EventTriggerConfigForm({
  config,
  onChange,
  allowedTriggers,
  locationScopeAccessStatus = 'accessible',
  locationScopeFolders,
}: EventTriggerConfigFormProps): JSX.Element {
  const {classes, cx} = useStyles();
  // Tracks a fresh data-type selection so the scope stays unselected — and the trigger section
  // stays hidden — until the user explicitly picks a target. Loaded/edited routines (which arrive
  // with a resolved scope) skip this gating so their scope and trigger render immediately.
  const awaitingScopeSelectionRef = useRef(false);
  const [selectedScope, setSelectedScope] = useState<TriggerScope | undefined>(() =>
    deriveScope(config, awaitingScopeSelectionRef.current)
  );

  const objectTypeSelectData = useMemo(
    () =>
      allowedTriggers.map((trigger) => ({
        value: trigger.objectType,
        label: getObjectTypeDisplayLabel(trigger.objectType),
      })),
    [allowedTriggers]
  );

  const triggerByObjectType = useMemo(() => {
    const byObjectType = new Map<string, AllowedEventTrigger>();
    for (const trigger of allowedTriggers) {
      byObjectType.set(trigger.objectType, trigger);
    }
    return byObjectType;
  }, [allowedTriggers]);

  const selectedObjectType = config.object_type ?? '';
  const selectedTrigger = selectedObjectType === '' ? undefined : triggerByObjectType.get(selectedObjectType);
  const selectedObjectTypeUIConfig = getObjectTypeUIConfig(selectedObjectType);
  const selectedPluralObjectTypeLabel = selectedObjectTypeUIConfig?.pluralLabel ?? 'objects';
  const selectedObjectSearchType =
    getModelTypeConstantForObjectType(selectedObjectType) ?? selectedObjectType;
  const selectedSchemaSource = useMemo(() => {
    if (selectedTrigger?.schemaSearchType == null) {
      return null;
    }
    return selectedObjectTypeUIConfig?.schemaSource ?? null;
  }, [selectedObjectTypeUIConfig, selectedTrigger]);

  const {
    schemas: schemaOptions,
    loading: schemasLoading,
    error: schemasError,
  } = useRoutineSchemaOptions(selectedSchemaSource);

  const objectTypeData = useMemo(() => {
    const objectType = config.object_type ?? '';
    if (objectType === '' || objectTypeSelectData.some((option) => option.value === objectType)) {
      return objectTypeSelectData;
    }
    return [{value: objectType, label: getObjectTypeDisplayLabel(objectType)}, ...objectTypeSelectData];
  }, [config.object_type, objectTypeSelectData]);

  const fieldOptions = useMemo(
    () => (selectedTrigger?.fields ?? []).map((field) => ({value: field.field, label: field.displayName})),
    [selectedTrigger]
  );

  const selectedField = selectedTrigger?.fields.find((field) => field.field === config.field);

  const valueOptions = useMemo(
    () => (selectedField?.allowedValues ?? []).map((value) => ({value, label: value})),
    [selectedField]
  );

  const valuesData = useMemo(() => {
    const currentValues = config.to_values ?? [];
    const existingValues = new Set(valueOptions.map((v) => v.value));
    const missing = currentValues.filter((v) => !existingValues.has(v));
    if (missing.length === 0) {
      return valueOptions;
    }
    return [...valueOptions, ...missing.map((value) => ({value, label: value}))];
  }, [valueOptions, config.to_values]);

  const triggerKind = config.trigger_kind;
  const capabilities = deriveCapabilities({
    selectedSchemaSource,
    selectedTrigger,
    triggerKind,
  });

  useEffect(() => {
    // A concrete scope loading from config (instance/schema) clears the fresh-selection latch.
    if (config.object_ids != null || config.schema_id != null) {
      awaitingScopeSelectionRef.current = false;
    }
    setSelectedScope(
      deriveScope(
        {
          object_type: config.object_type,
          trigger_kind: config.trigger_kind,
          object_ids: config.object_ids,
          schema_id: config.schema_id,
        },
        awaitingScopeSelectionRef.current
      )
    );
  }, [config.object_type, config.trigger_kind, config.object_ids, config.schema_id]);

  const objectTypeLabel =
    selectedObjectType === '' ? 'Object' : getObjectTypeDisplayLabel(selectedObjectType);
  const commonNounObjectTypeLabel = toCommonNounLabel(objectTypeLabel);

  const handleTriggerKindChange = (nextTriggerKind: TriggerKind): void => {
    if (nextTriggerKind === OBJECT_CREATED) {
      if (selectedScope === 'instance') {
        setSelectedScope(undefined);
      }
      onChange({
        trigger_kind: OBJECT_CREATED,
        field: undefined,
        to_values: undefined,
        object_ids: undefined,
      });
      return;
    }
    onChange({
      trigger_kind: FIELD_CHANGED,
      field: config.field ?? '',
      to_values: config.to_values ?? [],
    });
  };

  const handleScopeChange = (nextScope: TriggerScope): void => {
    awaitingScopeSelectionRef.current = false;
    setSelectedScope(nextScope);
    if (nextScope === 'all') {
      onChange({schema_id: undefined, object_ids: undefined});
      return;
    }
    if (nextScope === 'schema') {
      // The form normalizer clears object_ids when schema_id is updated.
      onChange({schema_id: config.schema_id ?? ''});
      return;
    }
    // The form normalizer clears schema_id when object_ids is updated.
    onChange({object_ids: config.object_ids ?? [], folder_ids: undefined});
  };

  const handleObjectTypeChange = (value: string | null): void => {
    setSelectedScope(undefined);
    if (value == null) {
      awaitingScopeSelectionRef.current = false;
      onChange({
        object_type: '',
        trigger_kind: undefined,
        field: undefined,
        to_values: undefined,
        schema_id: undefined,
        object_ids: undefined,
        folder_ids: undefined,
      });
      return;
    }

    // The user is starting a fresh target selection; keep the scope unselected (and the trigger
    // section hidden) until they pick one, even when the data type resolves a single trigger kind.
    awaitingScopeSelectionRef.current = true;

    const nextAllowedTrigger = triggerByObjectType.get(value);
    const supportsCreation = nextAllowedTrigger?.supportsCreation ?? false;
    const supportsFieldChanges = (nextAllowedTrigger?.fields.length ?? 0) > 0;
    let nextTriggerKind: TriggerKind | undefined;
    if (supportsCreation && !supportsFieldChanges) {
      nextTriggerKind = OBJECT_CREATED;
    } else if (!supportsCreation && supportsFieldChanges) {
      nextTriggerKind = FIELD_CHANGED;
    }

    const updates: Partial<EventTriggerConfig> = {
      object_type: value,
      trigger_kind: nextTriggerKind,
      schema_id: undefined,
      object_ids: undefined,
      folder_ids: undefined,
    };
    if (nextTriggerKind === OBJECT_CREATED) {
      updates.field = undefined;
      updates.to_values = undefined;
    } else if (nextTriggerKind === FIELD_CHANGED) {
      updates.field = '';
      updates.to_values = [];
    } else {
      updates.field = undefined;
      updates.to_values = undefined;
    }
    onChange(updates);
  };

  const getCardClassName = (selected: boolean, disabled: boolean): string =>
    cx(classes.card, selected && classes.selectedCard, disabled && classes.disabledCard);

  // The trigger-event section only appears after the user has chosen a restriction.
  const restrictToSatisfied = (config.object_scopes?.length ?? 0) > 0;

  return (
    <Stack spacing="md">
      <Stack spacing={4} className={classes.section}>
        <RoutineFieldHeader title="Which data type should this agent run on?" />
        <Select
          placeholder="Select data type"
          value={config.object_type ?? ''}
          onChange={handleObjectTypeChange}
          data={objectTypeData}
          classNames={{dropdown: classes.selectDropdown}}
          size="md"
        />
      </Stack>

      {selectedTrigger != null && (
        <TargetScopeSection
          capabilities={capabilities}
          scope={selectedScope}
          commonNounLabel={commonNounObjectTypeLabel}
          pluralObjectTypeLabel={selectedPluralObjectTypeLabel}
          selectedObjectType={selectedObjectType}
          selectedObjectSearchType={selectedObjectSearchType}
          selectedSchemaSource={selectedSchemaSource}
          schemaOptions={schemaOptions}
          schemasLoading={schemasLoading}
          schemasError={schemasError}
          config={config}
          getCardClassName={getCardClassName}
          onChange={onChange}
          onScopeChange={handleScopeChange}
          searchInputContainerClassName={classes.searchInputContainer}
          sectionClassName={classes.section}
          selectDropdownClassName={classes.selectDropdown}
        />
      )}

      {selectedTrigger?.supportsFolderScope === true &&
        selectedScope != null &&
        selectedScope !== 'instance' && (
          <LocationScopeSection
            accessStatus={locationScopeAccessStatus}
            allFolders={locationScopeFolders}
            config={config}
            onChange={onChange}
          />
        )}

      {selectedTrigger != null && selectedScope != null && restrictToSatisfied && (
        <EventTypeSection
          capabilities={capabilities}
          triggerKind={triggerKind}
          commonNounLabel={commonNounObjectTypeLabel}
          config={config}
          fieldOptions={fieldOptions}
          valuesData={valuesData}
          getCardClassName={getCardClassName}
          nestedEventInputContainerClassName={classes.nestedEventInputContainer}
          onChange={onChange}
          onTriggerKindChange={handleTriggerKindChange}
          sectionClassName={classes.section}
          selectDropdownClassName={classes.selectDropdown}
        />
      )}
    </Stack>
  );
}
